import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Play } from "lucide-react";
import { animations } from "@/data/brandGuide";

export const AnimationDemo = () => {
  const [activeAnimation, setActiveAnimation] = useState<string | null>(null);

  const playAnimation = (animationName: string) => {
    setActiveAnimation(animationName);
    setTimeout(() => setActiveAnimation(null), 1000);
  };

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-xl font-semibold mb-2">Animaciones Disponibles</h3>
        <p className="text-sm text-muted-foreground mb-6">
          Sistema de animaciones para transiciones fluidas
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {animations.map((animation) => (
          <Card key={animation.name} className="overflow-hidden">
            <CardHeader>
              <CardTitle className="text-base">{animation.name}</CardTitle>
              <p className="text-xs text-muted-foreground">{animation.usage}</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="h-32 bg-muted rounded-lg flex items-center justify-center overflow-hidden">
                <div
                  className={`w-20 h-20 bg-primary rounded-lg ${
                    activeAnimation === animation.name ? `animate-${animation.name}` : ""
                  }`}
                />
              </div>
              <div className="space-y-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full"
                  onClick={() => playAnimation(animation.name)}
                >
                  <Play className="h-3 w-3 mr-2" />
                  Reproducir
                </Button>
                <div className="bg-muted p-2 rounded text-xs">
                  <code>animate-{animation.name}</code>
                  <br />
                  <span className="text-muted-foreground">
                    Duración: {animation.duration}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Hover Effects */}
      <div className="space-y-4">
        <h3 className="text-xl font-semibold">Efectos Hover</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="transition-transform duration-300 hover:scale-105 cursor-pointer">
            <CardContent className="p-6 text-center">
              <p className="font-medium">Scale on Hover</p>
              <code className="text-xs text-muted-foreground">hover:scale-105</code>
            </CardContent>
          </Card>

          <Card className="transition-shadow duration-300 hover:shadow-lg cursor-pointer">
            <CardContent className="p-6 text-center">
              <p className="font-medium">Shadow on Hover</p>
              <code className="text-xs text-muted-foreground">hover:shadow-lg</code>
            </CardContent>
          </Card>

          <Card className="transition-colors duration-300 hover:bg-primary/10 cursor-pointer">
            <CardContent className="p-6 text-center">
              <p className="font-medium">Color on Hover</p>
              <code className="text-xs text-muted-foreground">hover:bg-primary/10</code>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Transition Timing */}
      <div className="space-y-4">
        <h3 className="text-xl font-semibold">Duraciones de Transición</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {["75", "100", "150", "200", "300", "500", "700", "1000"].map((duration) => (
            <Card key={duration}>
              <CardContent className="p-4 text-center">
                <p className="font-medium text-sm mb-2">{duration}ms</p>
                <code className="text-xs text-muted-foreground">
                  duration-{duration === "75" ? "[75ms]" : duration === "100" ? "[100ms]" : duration}
                </code>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};
